import { Injectable } from '@angular/core';
import { get } from 'lodash';
import { AuthResult } from 'angular-auth-oidc-client';
import { BehaviorSubject } from 'rxjs';
import { Location } from '../shared/models/location';
import { User } from '../shared/models/user';
import { UserRole } from '../shared/models/user-role';
import { ApiResponse } from '../shared/interfaces/api-response';
import { SecureStorageService } from '../services/secure-storage.service';

const IMPERSONATION_TOKEN_KEY: string = 'impersonationToken';

@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  private userInfo: User;
  private authResult: AuthResult;
  public userInfo$: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  private impersonationAuthResult?: ApiResponse<string>;

  constructor(private secureStorage: SecureStorageService) {
    this.loadImpersonation();
  }

  initializeLogInUser(user: User): void {
    this.userInfo = user;
    this.userInfo$.next(user);
  }

  initializeCredentials(authResult: AuthResult): void {
    this.authResult = authResult;
  }

  setLocation(location: Location): void {
    this.userInfo.selectedLocation = location;
    this.userInfo$.next(this.userInfo);
  }

  setRole(role: UserRole): void {
    this.userInfo.selectedRole = role;
    this.userInfo.locationsForSelectedRole = get(role, 'locations');
    this.userInfo$.next(this.userInfo);
  }

  get UserInfo(): User {
    return this.userInfo;
  }

  get AuthResult(): AuthResult {
    return this.authResult;
  }

  get UserInfo$(): BehaviorSubject<User> {
    return this.userInfo$;
  }

  isAuthenticated(): boolean {
    return !!this.authResult?.access_token;
  }

  getAccessToken(): string {
    return this.getImpersonationAccessToken() ??  (this.authResult?.access_token || '');
  }

  getIdToken(): string {
    return this.authResult?.id_token || '';
  }

  authorizationHeaderValue(): string {
    return `dpop ${this.getAccessToken()}`;
  }

  ///////////Impersonating////////
  isImpersonating(): boolean {
    return !!this.impersonationAuthResult?.value;
  }

  getImpersonationAccessToken(): string {
    return this.impersonationAuthResult?.value;
  }

  startImpersonation(authResult: ApiResponse<string>): void {
    this.impersonationAuthResult = authResult;
    // Store in secure memory-only storage (not vulnerable to XSS like sessionStorage)
    if (authResult?.value) {
      this.secureStorage.setItem(IMPERSONATION_TOKEN_KEY, authResult.value);
    }
  }

  stopImpersonation(): void {
    this.impersonationAuthResult = null;
    sessionStorage.removeItem('impersonationToken');
    this.secureStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  }

  loadImpersonation(): void {
    // Load from secure storage (memory-only, cleared on page refresh for security)
    const token: string | null = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
    if (token) {
      this.impersonationAuthResult = { value: token } as ApiResponse<string>;
    }
  }
}
